import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash-es';
import clsx from 'clsx';
import {
  OrderInfoResponse,
  getGetPortfolioQueryKey,
  useCancelAllActiveOrdersTrade,
  useCancelTrade,
  useMyTrade,
} from '@ping/api';
import { OVERRIDE_ON_ERROR } from '@ping/constants/override-on-error.constant';
import { t } from '@ping/helpers';
import { ButtonA11Y, Modal, TableWithVirtualList, Text, Toast } from '@ping/uikit';
import { queryClient } from '@ping/providers/ReactQueryProvider';
import NoDataIcon from '@ping/assets/Icon/no-data-found.svg';
import {
  isBusinessLoginSelector,
  isUserSupportSelector,
  useUserInformationStore,
} from '@ping/stores/userInformation.store';
import { shouldConfirmCancellingAnotherMembersOrder } from '@user/business/order-cancel.utils';

import getColumnsList from './getColumnsList';

type TBusinessOrder = OrderInfoResponse & {
  belongsToCurrentBusinessTrader?: boolean;
  mayBeCancelledByCurrentBusinessMember?: boolean;
};

import style from './style.module.scss';

const OpenOrdersTable = ({ selectedTab, isUserLoggedIn, className = '' }: IExchangeOrdersTabProps) => {
  const isUserSupport = useUserInformationStore(isUserSupportSelector);
  const isBusinessLogin = useUserInformationStore(isBusinessLoginSelector);
  const [sortState, setSortState] = useState<Object>({ id: 'createdAt', desc: true });
  const [orderPendingConfirmation, setOrderPendingConfirmation] = useState<TBusinessOrder | null>(null);
  // Open Orders
  const {
    data: openOrders,
    isLoading: isOpenOrdersLoading,
    queryKey: openOrdersKey,
  } = useMyTrade({ query: { enabled: !isUserSupport && isUserLoggedIn ? selectedTab === 'openOrders' : false } });
  // Delete Order
  const {
    mutate: deleteOrder,
    isLoading: isDeleteOrderLoading,
    variables: deleteOrderVariables,
  } = useCancelTrade(OVERRIDE_ON_ERROR);
  // Delete All Orders
  const { mutate: deleteAllOrders, isLoading: isDeleteAllOrdersLoading } = useCancelAllActiveOrdersTrade();

  const sendDeleteOrder = (id: string) =>
    deleteOrder(
      {
        orderId: id,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(openOrdersKey);
          queryClient.invalidateQueries(getGetPortfolioQueryKey());
          Toast.success({ title: t('The selected order has been deleted successfully') });
        },
        onError: () => {
          Toast.error({ title: t('An error occurred while deleting the selected order') });
        },
      }
    );

  const handleDeleteOrder = (order: TBusinessOrder) => {
    if (
      shouldConfirmCancellingAnotherMembersOrder({
        isBusinessLogin,
        belongsToCurrentBusinessTrader: order.belongsToCurrentBusinessTrader,
        mayBeCancelledByCurrentBusinessMember: order.mayBeCancelledByCurrentBusinessMember,
      })
    ) {
      setOrderPendingConfirmation(order);
      return;
    }

    if (order.orderId) {
      sendDeleteOrder(order.orderId);
    }
  };

  const handleDeleteAllOrders = () => {
    deleteAllOrders(null, {
      onSuccess: () => {
        queryClient.invalidateQueries(openOrdersKey);
        queryClient.invalidateQueries(getGetPortfolioQueryKey());
        Toast.success({ title: t('All the orders deleted successfully') });
      },
      onError: () => {
        Toast.error({ title: t('An error occurred while deleting all the orders') });
      },
    });
  };

  const openOrdersList = getColumnsList({
    openOrders,
    deleteOrderVariables,
    isDeleteOrderLoading,
    isDeleteAllOrdersLoading,
    handleDeleteOrder,
    handleDeleteAllOrders,
    isBusinessLogin,
  });

  //TODO: update the cache for all orders that were canceled simultaneously
  useEffect(() => {
    queryClient.invalidateQueries(openOrdersKey);
  }, [openOrders]);

  if (!isOpenOrdersLoading && (!openOrders || openOrders?.length === 0)) {
    return (
      <div
        className={clsx('trading-records__no-data-icon', {
          [style['open-orders-table__no-data-icon-margin-top']]: isUserLoggedIn,
        })}
      >
        <NoDataIcon />
      </div>
    );
  }

  return (
    <div className={className}>
      <TableWithVirtualList
        data={openOrders || []}
        columns={openOrdersList}
        classNameContainer={style['open-orders-table']}
        isLoading={isOpenOrdersLoading}
        initialState={
          isEmpty(sortState)
            ? {}
            : {
                sortBy: [sortState],
              }
        }
        getHeaderProps={column => ({
          ...column.getSortByToggleProps(),
          onClick: event => {
            setSortState(
              column.isSorted && column.isSortedDesc
                ? {}
                : {
                    id: column.id,
                    desc:
                      column.isSorted && !column.isSortedDesc
                        ? true
                        : !column.isSorted && column.isSortedDesc === undefined
                        ? false
                        : undefined,
                  }
            );
            return column.getSortByToggleProps().onClick(event);
          },
        })}
      />
      <Modal.Root
        isOpen={orderPendingConfirmation !== null}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setOrderPendingConfirmation(null);
          }
        }}
      >
        <Modal.Portal>
          <Modal.Header>
            <Modal.Title>{t('Cancel this order?')}</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Content>
            <Text body='regular'>{t('This order belongs to another Business member.')}</Text>
          </Modal.Content>
          <Modal.Actions>
            <ButtonA11Y
              primary
              onPress={() => {
                const orderId = orderPendingConfirmation?.orderId;
                setOrderPendingConfirmation(null);
                if (orderId) {
                  sendDeleteOrder(orderId);
                }
              }}
            >
              {t('Cancel order')}
            </ButtonA11Y>
          </Modal.Actions>
        </Modal.Portal>
      </Modal.Root>
    </div>
  );
};

export default OpenOrdersTable;
